import React from "react";
import { AiOutlineSend } from "react-icons/ai";
import { MdCheck, MdUndo } from "react-icons/md";
import "./DrawingToolbar.css";

const COLORS = [
    { value: "#FACC15", name: "Yellow" },
    { value: "#FB7185", name: "Pink" },
    { value: "#38BDF8", name: "Blue" },
    { value: "#FFFFFF", name: "White" },
];

const STROKES = [
    { value: 4, name: "Thin" },
    { value: 6, name: "Medium" },
    { value: 10, name: "Thick" },
];

interface DrawingToolbarProps {
    color: string;
    width: number;
    hasStrokes: boolean;
    isSending?: boolean;
    onColorChange: (color: string) => void;
    onWidthChange: (width: number) => void;
    onUndo: () => void;
    onDiscard?: () => void;
    onSend?: () => void;
}

export default function DrawingToolbar({
    color,
    width,
    hasStrokes,
    isSending = false,
    onColorChange,
    onWidthChange,
    onUndo,
    onDiscard,
    onSend,
}: DrawingToolbarProps) {
    return (
        <div
            className="drawing-toolbar"
            role="group"
            aria-label="Drawing tools"
            onPointerDown={event => event.stopPropagation()}
            // Clicking a tool doesn't move focus here, so Enter and Esc keep sending and discarding.
            onMouseDown={event => event.preventDefault()}
            onClick={event => event.stopPropagation()}
        >
            <div className="drawing-toolbar__layout">
                <div className="drawing-toolbar__group" role="group" aria-label="Color">
                    <span className="drawing-toolbar__label">Color</span>
                    <div className="drawing-toolbar__options">
                        {COLORS.map(option => (
                            <button
                                key={option.value}
                                type="button"
                                className="drawing-toolbar__swatch"
                                style={{ backgroundColor: option.value }}
                                title={option.name}
                                aria-label={`${option.name} drawing color`}
                                aria-pressed={color === option.value}
                                onClick={() => onColorChange(option.value)}
                            >
                                {color === option.value && <MdCheck size={18} aria-hidden="true" />}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="drawing-toolbar__group" role="group" aria-label="Stroke size">
                    <span className="drawing-toolbar__label">Stroke</span>
                    <div className="drawing-toolbar__options">
                        {STROKES.map(option => (
                            <button
                                key={option.value}
                                type="button"
                                className="drawing-toolbar__button drawing-toolbar__stroke"
                                title={`${option.name} stroke`}
                                aria-label={`${option.name} stroke`}
                                aria-pressed={width === option.value}
                                onClick={() => onWidthChange(option.value)}
                            >
                                <span
                                    className="drawing-toolbar__stroke-preview"
                                    style={{ width: option.value + 2, height: option.value + 2 }}
                                />
                            </button>
                        ))}
                    </div>
                </div>

                <div className="drawing-toolbar__actions" role="group" aria-label="Drawing actions">
                    <button
                        type="button"
                        className="drawing-toolbar__button"
                        title="Undo last stroke"
                        aria-label="Undo last stroke"
                        disabled={!hasStrokes || isSending}
                        onClick={onUndo}
                    >
                        <MdUndo size={19} aria-hidden="true" />
                    </button>
                    <span className="drawing-toolbar__divider" aria-hidden="true" />
                    <button
                        type="button"
                        className="drawing-toolbar__discard"
                        title="Discard drawing (Esc)"
                        disabled={isSending}
                        onClick={onDiscard}
                    >
                        Discard
                    </button>
                    <button
                        type="button"
                        className="drawing-toolbar__send"
                        title="Post as a comment (Enter)"
                        disabled={!hasStrokes || isSending}
                        onClick={onSend}
                    >
                        {isSending ? (
                            <span
                                className="loading loading-spinner loading-xs"
                                aria-hidden="true"
                            />
                        ) : (
                            <AiOutlineSend size={15} aria-hidden="true" />
                        )}
                        Send
                    </button>
                </div>
            </div>
        </div>
    );
}
